import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { api, CATEGORIES, slugify } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { EmptyState } from "@/components/EmptyState";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const FILTERS = ["All", ...CATEGORIES, "New Arrivals"];

const SHOP_SUBCATEGORIES = {
  "Gemstone Jewellery": ["Necklace", "Bracelet", "Earrings", "Pendants"],
  "Fashion Jewellery": ["Necklace", "Bracelet", "Earrings", "Pendants"],
};

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const category = searchParams.get("category") || "All";
  const subcategory = searchParams.get("subcategory") || "";
  const [sort, setSort] = useState("newest");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [products, setProducts] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setProducts(null);
    api
      .get("/products", {
        params: {
          category: category === "All" ? undefined : category,
          subcategory: subcategory || undefined,
          sort,
          search: debounced || undefined,
        },
      })
      .then((r) => setProducts(r.data.products))
      .catch(() => setProducts([]));
  }, [category, subcategory, sort, debounced]);

  return (
    <main data-testid="catalogue-page" className="min-h-screen bg-cream px-4 py-16 sm:px-8 lg:px-16 lg:py-24">
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold-muted">
            Aarmira — Fashion and Fortune
          </p>
          <h1 className="mt-3 font-serif text-4xl tracking-tight text-espresso sm:text-5xl">
            The Collection
          </h1>
        </motion.div>

        <div className="mt-10 flex flex-col gap-6 border-y border-gold/20 py-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 lg:mx-0 lg:flex-wrap lg:px-0">
            {FILTERS.map((c) => (
              <button
                key={c}
                data-testid={`catalogue-filter-category-${slugify(c)}`}
                onClick={() => {
                  if (c === "All") {
                    searchParams.delete("category");
                    setSearchParams(searchParams, { replace: true });
                  } else {
                    setSearchParams({ category: c }, { replace: true });
                  }
                }}
                className={`shrink-0 border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-all duration-300 active:scale-[0.97] ${
                  category === c
                    ? "border-gold bg-gold text-espresso"
                    : "border-espresso/15 text-espresso/60 hover:border-gold hover:text-espresso"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
          {SHOP_SUBCATEGORIES[category] && (
            <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4 lg:mx-0 lg:flex-wrap lg:px-0">
              {["All", ...SHOP_SUBCATEGORIES[category]].map((s) => (
                <button
                  key={s}
                  data-testid={`catalogue-filter-subcategory-${slugify(s)}`}
                  onClick={() =>
                    setSearchParams(
                      s === "All" ? { category } : { category, subcategory: s },
                      { replace: true }
                    )
                  }
                  className={`shrink-0 border px-3.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.18em] transition-all duration-300 active:scale-[0.97] ${
                    (s === "All" && !subcategory) || subcategory === s
                      ? "border-espresso bg-espresso text-gold"
                      : "border-gold/40 text-gold-muted hover:border-gold hover:text-gold-accent"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className="relative flex-1 lg:w-56">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-espresso/40" />
              <input
                data-testid="catalogue-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search pieces…"
                className="w-full border border-espresso/15 bg-transparent py-2.5 pl-9 pr-3 text-sm text-espresso outline-none transition-colors placeholder:text-espresso/35 focus:border-gold"
              />
            </div>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger
                data-testid="catalogue-sort-select"
                className="w-44 rounded-none border-espresso/15 bg-transparent font-mono text-[10px] uppercase tracking-[0.15em]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-none border-gold/30 bg-cream">
                <SelectItem value="newest" data-testid="catalogue-sort-newest">Newest</SelectItem>
                <SelectItem value="price_asc" data-testid="catalogue-sort-price-asc">Price: Low to High</SelectItem>
                <SelectItem value="price_desc" data-testid="catalogue-sort-price-desc">Price: High to Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mt-12" data-testid="catalogue-product-grid">
          {products === null ? (
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-cream-dark" />
                  <div className="mt-4 h-4 w-2/3 bg-cream-dark" />
                  <div className="mt-2 h-3 w-1/3 bg-cream-dark" />
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <EmptyState
              testid="catalogue-empty-state"
              title={debounced ? "No pieces match your search" : "This shelf awaits its treasures"}
              body={
                debounced
                  ? "Try a different name, or browse the full collection."
                  : "Pieces for this category are being prepared in the atelier. Explore the rest of the collection in the meantime."
              }
            />
          ) : (
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} testid={`catalogue-product-card-${p.id}`} />
              ))}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default Shop;
